<template>
  <div class="toggleCardWrapper">
    <button
      type="button"
      class="toggleCard"
      :class="{ '-active': itemModel.vars.value }"
      :disabled="isDisabled"
      :aria-pressed="!!itemModel.vars.value"
      @click="toggle"
    >
      <span v-if="icon" class="toggleCard__icon" :style="iconStyle">
        <FontAwesomeIcon :icon="'fa-light fa-' + icon" />
      </span>
      <span class="toggleCard__text">
        {{ itemModel.vars.label }}{{ itemModel.vars.required ? "*" : "" }}
        <span
          v-if="itemModel.vars.help"
          v-tooltip="{ title: itemModel.vars.help, textWrap: true }"
          class="form-info ms-1"
        >
          <FontAwesomeIcon icon="fa-light fa-circle-question" />
        </span>
        <span v-if="hint" class="toggleCard__hint">{{ hint }}</span>
      </span>
      <span class="toggleCard__check">
        <FontAwesomeIcon icon="fa-light fa-check" />
      </span>
    </button>
    <p v-if="itemModel.vars.value && activeMessage" class="toggleCard__message">
      {{ activeMessage }}
    </p>

    <BlockLoader :loading="!!itemModel.loading" />
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import BlockLoader from "@/components/blocks/BlockLoader.vue";

// Composant générique autonome (attr['data-toggle-card'], cf. formManager.isToggleCardType()) :
// case à cocher boolean rendue comme une carte cliquable (icône + label + sous-texte + check
// circulaire), avec un message optionnel affiché quand actif - contenu 100% piloté par
// model.vars (label/value) et model.vars.attr (data-icon/data-hint/data-active-message), aucun
// texte propre au composant, au même titre que PlainCheckboxTypeComponent/ChoiceChipTypeComponent.
export default {
  name: "ToggleCardTypeComponent",
  components: { FontAwesomeIcon, BlockLoader },
  props: {
    model: {
      type: Object,
      required: true,
    },
  },
  emits: ["update", "toggle"],
  data() {
    return {
      itemModel: this.model,
    };
  },
  computed: {
    // Aligné sur CheckboxTypeComponent/SelectTypeComponent (revue Loïc, ST304-TA-4918) : readonly
    // et disabled désactivent tous deux les interactions.
    isDisabled() {
      return !!(this.itemModel.vars.disabled || (this.itemModel.vars.attr && this.itemModel.vars.attr.readonly));
    },
    icon() {
      return this.itemModel.vars.attr?.["data-icon"] || null;
    },
    hint() {
      return this.itemModel.vars.attr?.["data-hint"] || null;
    },
    activeMessage() {
      return this.itemModel.vars.attr?.["data-active-message"] || null;
    },
    // Couleurs d'icône indépendantes par champ (ex. "être rappelé" en vert, "je ne sais pas" en
    // bleu marine par défaut) - ignorées une fois actif, l'icône reprend le style par défaut du
    // composant, même code pour tous les champs.
    iconStyle() {
      if (this.itemModel.vars.value) {
        return {};
      }
      const background = this.itemModel.vars.attr?.["data-icon-bg"];
      const color = this.itemModel.vars.attr?.["data-icon-color"];
      return background || color ? { background, color } : {};
    },
  },
  methods: {
    toggle() {
      this.itemModel.vars.value = !this.itemModel.vars.value;
      // "wantsCallback" : distingue "jamais cliqué" de "répondu non" - processField()
      // (form-manager.js) n'envoie la clé que si touched !== false (cf. CandidacyLandingFormView,
      // qui initialise wantsCallback.touched = false au bootstrap ; "unknownTrainingChoice" n'a pas cette
      // clé et est donc toujours envoyé).
      this.itemModel.touched = true;
      this.$emit("update", this.itemModel.vars.value);
      if (this.itemModel.vars.toggler_target) {
        this.$emit("toggle", this.itemModel);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// FormComponent.vue met chaque champ dans un ".form-group" en display:flex - sans largeur
// explicite ici, cet élément (racine du composant) se réduirait au contenu du bouton, qui
// grandirait ensuite quand le message actif apparaît (texte plus long).
.toggleCardWrapper {
  width: 100%;
  position: relative;
}

.toggleCard {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  // Revue Loïc (RGAA 3.3) : $landing-border sur $landing-field-bg ne tient que 1,23:1 -
  // $landing-muted tient 4,56:1. :hover remonté à $landing-navy pour garder un palier distinct
  // (sinon identique au repos).
  border: 1.5px dashed $landing-muted;
  border-radius: 9px;
  padding: 9px 13px;
  background: $landing-field-bg;
  cursor: pointer;
  transition: 0.15s;
  font-family: inherit;
  text-align: left;

  &:hover {
    border-color: $landing-navy;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid $landing-green-dark;
    outline-offset: 1px;
  }

  &__icon {
    width: 30px;
    height: 30px;
    min-width: 30px;
    border-radius: 8px;
    background: $landing-bg;
    color: $landing-navy;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__text {
    flex: 1;
    min-width: 0;
    font-size: 12.5px;
    font-weight: 700;
    color: $landing-navy;
  }

  &__hint {
    display: block;
    font-size: 10.5px;
    font-weight: 500;
    color: $landing-muted-dark;
    margin-top: 1px;
  }

  &__check {
    width: 18px;
    height: 18px;
    min-width: 18px;
    border-radius: 50%;
    // Revue Loïc (RGAA 3.3) : rond vide au repos, bordure à $landing-muted (4,56:1) ; l'icône
    // blanche reste invisible sur le fond blanc jusqu'à l'état actif (cf. .-active ci-dessous).
    border: 2px solid $landing-muted;
    background: $white;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $white;
    font-size: 10px;
  }

  // Même rendu qu'une carte sélectionnée de ChoiceCardListTypeComponent (&__card.-selected).
  &.-active {
    background: rgba($success, 0.1);
    border-color: $success;
    border-style: solid;

    .toggleCard__check {
      background: $success;
      border-color: $success;
    }
  }
}

.toggleCard__message {
  text-align: center;
  padding: 20px 14px;
  margin: 10px 0 0;
  color: $landing-navy;
  font-size: 12px;
  line-height: 1.5;
  background: $landing-bg;
  border-radius: 10px;
}
</style>
